<template>
  <section
    id="home-projects"
    class="past-projects"
    aria-labelledby="past-projects-title"
  >
    <div class="past-projects__heading">
      <h2 id="past-projects-title">{{ copy.heading }}</h2>
    </div>

    <div
      ref="viewportRef"
      class="past-projects__viewport"
      :class="{ 'is-dragging': isDragging }"
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
      @pointerdown="handlePointerDown"
      @pointermove="handlePointerMove"
      @pointerup="endDrag"
      @pointercancel="endDrag"
    >
      <div
        ref="trackRef"
        class="past-projects__track"
        :style="{ transform: `translate3d(${offset}px, 0, 0)` }"
      >
        <div ref="groupRef" class="past-projects__group">
          <article
            v-for="project in projects"
            :key="project.targetId"
            class="past-projects__card"
            :data-target-id="project.targetId"
            @click="handleExplore(project.targetId)"
          >
            <img
              class="past-projects__image"
              :src="project.image"
              :alt="project.title"
              draggable="false"
            />
            <div class="past-projects__body">
              <h3>{{ project.title }}</h3>
              <button type="button" :data-target-id="project.targetId">
                <span>{{ copy.explore }}</span>
                <img
                  class="past-projects__arrow"
                  src="@/assets/straithub/arrow-right.png"
                  alt=""
                  draggable="false"
                />
              </button>
            </div>
          </article>
        </div>

        <div class="past-projects__group" aria-hidden="true">
          <article
            v-for="project in projects"
            :key="`clone-${project.targetId}`"
            class="past-projects__card"
            :data-target-id="project.targetId"
            @click="handleExplore(project.targetId)"
          >
            <img
              class="past-projects__image"
              :src="project.image"
              alt=""
              draggable="false"
            />
            <div class="past-projects__body">
              <h3>{{ project.title }}</h3>
              <button
                type="button"
                tabindex="-1"
                :data-target-id="project.targetId"
              >
                <span>{{ copy.explore }}</span>
                <img
                  class="past-projects__arrow"
                  src="@/assets/straithub/arrow-right.png"
                  alt=""
                  draggable="false"
                />
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const SCROLL_SPEED = 42;
const START_OFFSET = -52;

const router = useRouter();
const { locale } = useI18n();

const viewportRef = ref<HTMLElement | null>(null);
const trackRef = ref<HTMLElement | null>(null);
const groupRef = ref<HTMLElement | null>(null);
const offset = ref(START_OFFSET);
const groupWidth = ref(0);
const isHovering = ref(false);
const isDragging = ref(false);
const isReducedMotion = ref(false);

let resizeObserver: ResizeObserver | null = null;
let animationFrame = 0;
let lastFrameTime: number | undefined;
let suppressClick = false;
const dragState = {
  pointerId: null as number | null,
  startX: 0,
  startOffset: START_OFFSET,
  moved: false,
  targetId: "",
};

const copy = computed(() =>
  locale.value === "zh"
    ? { heading: "项目案例", explore: "了解更多" }
    : { heading: "Past Projects", explore: "Explore More" }
);

const projects = computed(() => {
  const isZh = locale.value === "zh";

  return [
    {
      targetId: "project-1",
      title: isZh ? "AI 服务器部署" : "AI Server Deployment",
      image: require("@/assets/straithub/past_projects/1-card.jpg"),
    },
    {
      targetId: "project-2",
      title: isZh ? "RDHx 液冷背门改造" : "RDHx Retrofit",
      image: require("@/assets/straithub/past_projects/2-card.jpg"),
    },
    {
      targetId: "project-3",
      title: isZh ? "数据中心负载测试" : "Data Center Load Bank Testing",
      image: require("@/assets/straithub/past_projects/3-card.jpg"),
    },
    {
      targetId: "project-5",
      title: isZh
        ? "AIDC 液冷二次侧一站式服务集成"
        : "AIDC Liquid Cooling Secondary Side One-Stop Service Integration",
      image: require("@/assets/straithub/past_projects/4-card.jpg"),
    },
    {
      targetId: "project-4",
      title: isZh ? "预制模块化数据中心" : "Prefabricated Modular Data Center",
      image: require("@/assets/straithub/past_projects/5.png"),
    },
  ];
});

const wrapOffset = (value: number) => {
  if (!groupWidth.value) return value;

  let next = value % groupWidth.value;
  if (next > 0) next -= groupWidth.value;
  return next;
};

const applyOffset = (value: number) => {
  offset.value = wrapOffset(value);
};

const measure = () => {
  groupWidth.value = groupRef.value?.offsetWidth || 0;
  applyOffset(offset.value);
};

const tick = (time: number) => {
  if (lastFrameTime === undefined) lastFrameTime = time;
  const delta = Math.min((time - lastFrameTime) / 1000, 0.064);
  lastFrameTime = time;

  if (!isReducedMotion.value && !isHovering.value && !isDragging.value) {
    applyOffset(offset.value - SCROLL_SPEED * delta);
  }

  animationFrame = window.requestAnimationFrame(tick);
};

const startAnimation = () => {
  if (animationFrame) return;
  lastFrameTime = undefined;
  animationFrame = window.requestAnimationFrame(tick);
};

const stopAnimation = () => {
  if (!animationFrame) return;
  window.cancelAnimationFrame(animationFrame);
  animationFrame = 0;
};

const navigateToProject = (targetId: string) => {
  router.push({ path: "/projects", hash: `#${targetId}` });
};

const handlePointerDown = (event: PointerEvent) => {
  if (
    !groupWidth.value ||
    (event.pointerType === "mouse" && event.button !== 0)
  ) {
    return;
  }

  const exploreButton = (event.target as Element | null)?.closest<HTMLElement>(
    "[data-target-id]"
  );

  dragState.pointerId = event.pointerId;
  dragState.startX = event.clientX;
  dragState.startOffset = offset.value;
  dragState.moved = false;
  dragState.targetId = exploreButton?.dataset.targetId || "";
  suppressClick = false;
  isDragging.value = true;
  viewportRef.value?.setPointerCapture?.(event.pointerId);
};

const handlePointerMove = (event: PointerEvent) => {
  if (!isDragging.value || dragState.pointerId !== event.pointerId) return;

  const distance = event.clientX - dragState.startX;
  if (Math.abs(distance) > 4) {
    dragState.moved = true;
    suppressClick = true;
  }
  applyOffset(dragState.startOffset + distance);
};

const endDrag = (event: PointerEvent) => {
  if (!isDragging.value || dragState.pointerId !== event.pointerId) return;

  const targetId = dragState.moved ? "" : dragState.targetId;

  viewportRef.value?.releasePointerCapture?.(event.pointerId);
  isDragging.value = false;
  dragState.pointerId = null;
  dragState.targetId = "";

  if (targetId) {
    suppressClick = true;
    navigateToProject(targetId);
    window.setTimeout(() => {
      suppressClick = false;
    }, 180);
    return;
  }

  if (dragState.moved) {
    window.setTimeout(() => {
      suppressClick = false;
    }, 180);
  }
};

const handleExplore = (targetId: string) => {
  if (suppressClick) return;
  navigateToProject(targetId);
};

onMounted(async () => {
  isReducedMotion.value = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  await nextTick();
  measure();

  if (typeof ResizeObserver !== "undefined" && groupRef.value) {
    resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(groupRef.value);
  } else {
    window.addEventListener("resize", measure);
  }

  startAnimation();
});

onActivated(startAnimation);
onDeactivated(stopAnimation);

onUnmounted(() => {
  stopAnimation();
  resizeObserver?.disconnect();
  window.removeEventListener("resize", measure);
});
</script>

<style lang="less" scoped>
.past-projects {
  width: 100%;
  height: 560px;
  padding: 0;
  overflow: hidden;
  background: #fff;
}

.past-projects__heading {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 118px auto 56px;
  padding: 0;

  h2 {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0;
    padding-left: 1rem;
    border-left: 0.25rem solid var(--font-color-blue-dark);
    color: var(--font-color-black-light);
    font-size: var(--font-size-big);
    font-weight: var(--font-weight-plus);
    line-height: normal;
    letter-spacing: 0;
  }
}

.past-projects__viewport {
  width: 100%;
  cursor: grab;
  touch-action: pan-y;
  user-select: none;

  &.is-dragging {
    cursor: grabbing;
  }
}

.past-projects__track {
  display: flex;
  align-items: stretch;
  width: max-content;
  will-change: transform;
}

.past-projects__group {
  display: flex;
  gap: 13.5px;
  padding-right: 13.5px;
}

.past-projects__card {
  flex: 0 0 291.75px;
  width: 291.75px;
  height: 258.75px;
  overflow: hidden;
  background: #f5f5f5;
}

.past-projects__image {
  display: block;
  width: 100%;
  height: 168px;
  object-fit: cover;
  pointer-events: none;
}

.past-projects__body {
  height: 90px;
  padding: 18px;

  h3 {
    min-height: 42px;
    color: #000000;
    font-family: "HarmonyOS_Regular";
    font-size: 13.5px;
    font-weight: 600;
    line-height: 18px;
  }

  button {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #0757ff;
    font-family: inherit;
    font-size: 10.5px;
    line-height: 15px;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid rgba(7, 87, 255, 0.35);
      outline-offset: 0.3rem;
    }
  }
}

.past-projects__arrow {
  display: block;
  width: 12px;
  height: 12px;
  object-fit: contain;
  pointer-events: none;
}

@media (max-width: 639px) {
  .past-projects {
    height: auto;
    padding: 5.5rem 0 5rem;
  }

  .past-projects__heading {
    justify-content: center;
    margin: 0 0 2.6rem;
    padding: 0 2rem;

    h2 {
      font-size: var(--font-size-big);
    }
  }

  .past-projects__card {
    flex-basis: min(78vw, 26rem);
    width: min(78vw, 26rem);
    height: 23rem;
  }

  .past-projects__image {
    height: 15.5rem;
  }
  .past-projects__body {
    height: 7.5rem;
  }
  .past-projects__body h3 {
    font-size: 1.2rem;
    line-height: 1.6rem;
  }
  .past-projects__body button {
    font-size: 1rem;
    line-height: 1.3rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .past-projects__card {
    transition: none;
  }
}
</style>
